import * as React from "react";
import { twMerge } from "tailwind-merge";
import { Presence, SlideIn } from "@/primitives/motion";
import { BackButton } from "../components/BackButton";
import { StickyRootNode } from "../components/StickyRootNode";
import { sizeStyles } from "../constants";
import type { HierarchyNode, StickyNodeState } from "../types";
import { findNodeById } from "../utils";

interface SplitLayoutProps<T extends HierarchyNode> {
  data: T[];
  currentItems: T[];
  navigationStack: T[];
  selectedId: string | null;
  variant: "subtle" | "solid";
  size: "xs" | "sm" | "md";
  showBullets: boolean;
  rootNodeStickyState: StickyNodeState<T>;
  renderNodeList: (items: T[], showConnector: boolean) => React.ReactNode;
  renderDetailContent: (parent: T | null) => React.ReactNode;
  renderEmptyState?: () => React.ReactNode;
  onNavigateBack: () => void;
}

export function SplitLayout<T extends HierarchyNode>({
  data,
  currentItems,
  navigationStack,
  selectedId,
  variant,
  size,
  showBullets,
  rootNodeStickyState,
  renderNodeList,
  renderDetailContent,
  renderEmptyState,
  onNavigateBack,
}: SplitLayoutProps<T>) {
  // In split layout, resolve selected node from full tree for nested selections
  const selectedNode = selectedId ? findNodeById(data, selectedId) : null;

  return (
    <>
      {/* Left Pane - Master List */}
      <div className="flex-1 min-w-0 border-r border-border-default flex flex-col relative">
        {/* Floating Sticky Root Node (only on root view) */}
        {navigationStack.length === 0 && rootNodeStickyState.position && rootNodeStickyState.node && (
          <StickyRootNode
            node={rootNodeStickyState.node}
            position={rootNodeStickyState.position}
            selectedId={selectedId}
            variant={variant}
            size={size}
            showBullets={showBullets}
          />
        )}

        {/* Back Button (when drilled down) */}
        {navigationStack.length > 0 && (
          <BackButton navigationStack={navigationStack} onNavigateBack={onNavigateBack} size={size} />
        )}

        {/* Master List Content */}
        <div className="flex-1 overflow-auto">{renderNodeList(currentItems, navigationStack.length === 0)}</div>
      </div>

      {/* Right Pane - Detail View */}
      <div className="flex-1 min-w-0 overflow-auto bg-background-body">
        <Presence mode="wait" presenceKey={selectedNode?.id || "empty"}>
          {selectedNode ? (
            <SlideIn direction="left" duration="fast">
              {renderDetailContent(selectedNode)}
            </SlideIn>
          ) : renderEmptyState ? (
            renderEmptyState()
          ) : (
            <div className={twMerge("text-text-muted text-center", sizeStyles[size].node, "py-8")}>
              Select an item to view details
            </div>
          )}
        </Presence>
      </div>
    </>
  );
}
